import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { probeWebGL2 } from "@/lib/webgl";
import useReducedMotion from "@/lib/useReducedMotion";
import { CINEMATIC_STATES, stateIndexAt, buildGraphModel, graphSignature, quantumVerified } from "@/lib/decisionGraph";
import { buildSemantics, paintGraph, getLegend, aggregateAgents } from "@/lib/decisionGraphLegend";
import { terminalKind } from "@/lib/decisionGraphLive";
import { parseSnapshotParams, frameIndexForSequence, snapshotNotice, runClassification, snapshotLegendVersion } from "@/lib/decisionGraphSnapshot";
import { parseDemoParams, shouldAutoStart, buildDemoPath } from "@/lib/decisionGraphDemo";
import { resolveGeo } from "@/lib/decisionGraphGeo";
import GlobeCanvas from "@/webgl/GlobeCanvas";
import AccessibleGraphSummary from "@/components/workspace/AccessibleGraphSummary";
import ContextHeader from "@/components/workspace/decisiongraph/ContextHeader";
import EventTimeline from "@/components/workspace/decisiongraph/EventTimeline";
import { HudTop, HudBottom } from "@/components/workspace/decisiongraph/SwarmHud";
import EvidenceRail from "@/components/workspace/decisiongraph/EvidenceRail";
import TerminalOverlay from "@/components/workspace/decisiongraph/TerminalOverlay";
import SnapshotBanner from "@/components/workspace/decisiongraph/SnapshotBanner";
import useRunStream, { isAuthError } from "@/components/workspace/decisiongraph/useRunStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEP_MS = 1400;

export default function DecisionGraphArea() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const snapshot = useMemo(() => parseSnapshotParams(params), [params]);
  const demo = useMemo(() => parseDemoParams(params), [params]);
  const autoStarted = useRef(false);
  const [verdict] = useState(() => probeWebGL2());
  const reducedMotion = useReducedMotion();
  const [scenarios, setScenarios] = useState([]);
  const [runs, setRuns] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [selected, setSelected] = useState(() => (snapshot ? { run_id: snapshot.runId, key: snapshot.runId, snapshot: true } : null));
  const [listStatus, setListStatus] = useState("loading");
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState(null);
  const [clusterExpanded, setClusterExpanded] = useState(false);

  const { recon, events, mode, status: streamStatus, connection, lastEventAt } = useRunStream(selected?.run_id);

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/decision-graph/scenarios`, { withCredentials: true });
      setScenarios(data.scenarios || []); setRuns(data.runs || []); setIdentity(data.identity || null);
      setListStatus("ready");
      return data;
    } catch (e) {
      setListStatus(isAuthError(e) ? "auth_lost" : "error");
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await loadList();
      if (!alive || !data || snapshot || demo) return;
      const first = (data.scenarios || [])[0]
        || (data.runs || []).map((r) => ({ run_id: r.run_id, label: r.run_id, module_id: r.module_id, key: r.run_id }))[0];
      if (first) setSelected(first); else setListStatus("empty");
    })();
    return () => { alive = false; };
  }, [loadList, snapshot, demo]);

  // Per-run reset of the viewport-local state.
  useEffect(() => { setCursor(0); setPlaying(false); setSelectedClusterId(null); setClusterExpanded(false); }, [selected?.run_id]);
  useEffect(() => { if (mode === "completed") loadList(); }, [mode, loadList]);

  const live = mode === "live";
  const runStatus = recon?.run?.status;
  const terminal = terminalKind(runStatus);

  // Live: the cursor tails the newest real event. Snapshot: pinned to the requested frame.
  useEffect(() => {
    if (!events.length) return;
    if (snapshot) { setCursor(frameIndexForSequence(events, snapshot.sequence)); setPlaying(false); return; }
    if (live || mode === "completed") setCursor(events.length - 1);
  }, [events, live, mode, snapshot]);

  // Replay auto-play starts once a completed Run's events have fully loaded; a
  // failed/cancelled Run opens on its last recorded frame (the record as it stands).
  useEffect(() => {
    if (mode !== "replay" || streamStatus !== "ready" || snapshot || events.length === 0) return;
    if (terminal && terminal !== "completed") { setCursor(events.length - 1); setPlaying(false); return; }
    if (events.length > 1) { setCursor(0); setPlaying(true); }
  }, [mode, streamStatus, events.length, snapshot, terminal]);

  useEffect(() => {
    if (!playing || live || events.length === 0) return undefined;
    if (cursor >= events.length - 1) { setPlaying(false); return undefined; }
    const timer = setTimeout(() => setCursor((c) => Math.min(c + 1, events.length - 1)), STEP_MS);
    return () => clearTimeout(timer);
  }, [playing, cursor, events, live]);

  const summary = recon?.run?.summary || {};
  const legendVersion = snapshotLegendVersion(recon);
  const legend = getLegend(legendVersion);
  const classification = runClassification(recon);

  const modelInput = useMemo(() => ({
    module_id: selected?.module_id || recon?.run?.module_id, summary,
    events: events.length ? events : (recon?.events || []), workUnits: recon?.work_units,
  }), [events, recon, selected, summary]);
  const sig = (events.length || recon) ? graphSignature(modelInput) : null;
  const modelCache = useRef({ sig: null, model: null });
  if (modelCache.current.sig !== sig) modelCache.current = { sig, model: sig ? buildGraphModel(modelInput) : null };
  const model = modelCache.current.model;

  const semantics = useMemo(() => buildSemantics({ recon, events }), [recon, events]);
  const stateIndex = events.length ? stateIndexAt(events, cursor) : 0;
  const painted = useMemo(
    () => (model ? paintGraph({ model, semantics, state: stateIndex, legendVersion }) : []),
    [model, semantics, stateIndex, legendVersion]);

  const clusters = useMemo(() => aggregateAgents(summary.agent_count || model?.agentCount || 0), [summary, model]);

  // Globe geo placement. DEMO runs map to a curated real-city set; real runs carry no
  // per-entity geo → their entities are 'unplaced' (never a fabricated coordinate).
  const isDemo = !!demo || recon?.run?.configuration?.demo === true || !!recon?.run?.demo_scenario;
  const geo = useMemo(
    () => resolveGeo({ model, moduleId: selected?.module_id || recon?.run?.module_id, isDemo }),
    [model, selected, recon, isDemo]);

  const evidence = recon?.evidence || [];
  const quantum = quantumVerified(summary, evidence);
  const integrityOk = recon?.integrity?.ok;
  const notice = snapshot && recon ? snapshotNotice({ runStatus, events, sequence: snapshot.sequence }) : null;
  const status = listStatus === "error" || listStatus === "auth_lost" ? "error"
    : listStatus === "empty" ? "empty" : selected ? streamStatus : "loading";
  const headerConnection = listStatus === "auth_lost" ? "auth_lost" : connection;

  const selectRun = useCallback((r) => {
    if (snapshot || (demo && !r?.live)) navigate("/app/decision-graph");
    setSelected(r);
  }, [snapshot, demo, navigate]);

  const startLive = useCallback(async (scenarioKey) => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/decision-graph/live/${scenarioKey}`, {}, { withCredentials: true });
      selectRun({ run_id: data.run_id, label: data.label, module_id: data.module_id, key: data.run_id, live: true });
      loadList();
    } catch (e) {
      if (isAuthError(e)) setListStatus("auth_lost");
    } finally { setStarting(false); }
  }, [selectRun, loadList]);

  // Investor demo: one click (or one bookmark) presses "Start live demo Run" once;
  // from there the real Run drives the verified live path hands-off.
  useEffect(() => {
    if (!shouldAutoStart({ demo, listStatus, canCreate: identity?.can_create, alreadyStarted: autoStarted.current })) return;
    autoStarted.current = true;
    startLive(demo.scenario);
  }, [demo, listStatus, identity, startLive]);
  const openDemo = useCallback((scenarioKey) => { const p = buildDemoPath(scenarioKey); if (p) navigate(p); }, [navigate]);

  const openLive = useCallback(() => {
    navigate("/app/decision-graph");
    setSelected({ run_id: snapshot.runId, key: snapshot.runId, module_id: recon?.run?.module_id, label: recon?.run?.module_id });
  }, [navigate, snapshot, recon]);

  const onSelectAgent = useCallback((id) => { setSelectedClusterId(id); }, []);
  const onZoom = useCallback((distance) => { setClusterExpanded(distance < 8); }, []);
  const currentSequence = events[cursor]?.sequence;

  return (
    <div className="h-screen flex flex-col bg-[var(--qn-background)] text-[var(--qn-foreground)] overflow-hidden" data-testid="decision-graph-area">
      <ContextHeader selected={selected} scenarios={scenarios} runs={runs} onSelect={selectRun}
        recon={recon} quantum={quantum} integrityOk={integrityOk} summary={summary} connection={headerConnection}
        live={live} lastEventAt={lastEventAt} snapshot={snapshot} classification={classification}
        canStartLive={!!identity?.can_create} starting={starting} onStartLive={startLive}
        demo={demo} onOpenDemo={openDemo} />

      <div className="flex-1 flex min-h-0">
        <div className="relative min-h-0" style={{ width: "68%" }} data-testid="decision-graph-viewport">
          {status === "loading" && <Centered><div className="w-9 h-9 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" /></Centered>}
          {status === "error" && <Centered><span className="text-[var(--qn-error)] text-sm" data-testid="dg-error">{t("decisionGraph.loadError")}</span></Centered>}
          {status === "empty" && <Centered><span className="text-[var(--qn-foreground-muted)] text-sm" data-testid="dg-empty">{t("decisionGraph.empty")}</span></Centered>}
          {status === "ready" && model && (
            verdict.supported
              ? <GlobeCanvas model={model} painted={painted} state={stateIndex} legend={legend}
                  semantics={semantics} reducedMotion={reducedMotion} onSelectAgent={onSelectAgent} onZoom={onZoom}
                  live={live} dimmed={!!terminal && terminal !== "completed"} geo={geo} eventKey={currentSequence} />
              : <AccessibleGraphSummary model={model} state={stateIndex} reason={verdict.reason} />
          )}
          {status === "ready" && model && verdict.supported && (
            <>
              <HudTop runId={selected?.run_id} moduleId={recon?.run?.module_id || selected?.module_id} state={stateIndex} model={model}
                events={events} cursor={cursor} live={live} connection={connection} />
              <HudBottom events={events} cursor={cursor} state={stateIndex} />
            </>
          )}
          {status === "ready" && <StateBadge index={stateIndex} reducedMotion={reducedMotion} live={live} />}
          {status === "ready" && terminal && terminal !== "completed" && !live && (
            <TerminalOverlay kind={terminal} reason={recon?.run?.failure_reason || recon?.run?.summary?.reason} reducedMotion={reducedMotion} />
          )}
          {status === "ready" && snapshot && recon && (
            <SnapshotBanner sequence={snapshot.sequence} notice={notice} classification={classification} onOpenLive={openLive} />
          )}
          {status === "ready" && (events.length > 0 || live) && (
            <EventTimeline events={events} cursor={cursor} playing={playing} live={live} runId={selected?.run_id}
              onScrub={(i) => { if (!live) setCursor(i); }}
              onPlay={() => { if (cursor >= events.length - 1) setCursor(0); setPlaying(true); }}
              onPause={() => setPlaying(false)}
              onRestart={() => { setCursor(0); setPlaying(true); }} />
          )}
        </div>

        <div className="border-l border-[var(--qn-border)] bg-[var(--qn-background-secondary)] min-h-0 overflow-y-auto" style={{ width: "32%" }}>
          <EvidenceRail recon={recon} summary={summary} quantum={quantum} integrityOk={integrityOk}
            evidence={evidence} verdict={verdict} legendVersion={legendVersion} clusters={clusters}
            selectedClusterId={selectedClusterId} clusterExpanded={clusterExpanded}
            onSelectCluster={setSelectedClusterId} onCloseCluster={() => setSelectedClusterId(null)} />
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="absolute inset-0 flex items-center justify-center">{children}</div>;
}

function StateBadge({ index, reducedMotion, live }) {
  const { t } = useTranslation();
  const s = CINEMATIC_STATES[index] || CINEMATIC_STATES[0];
  const anim = reducedMotion ? {} : { initial: { opacity: 0, y: -6 }, animate: { opacity: 1, y: 0 } };
  return (
    <motion.div key={index} {...anim}
      className="absolute top-8 left-4 rounded-xl bg-[var(--qn-glass)] backdrop-blur border border-[var(--qn-border)] px-4 py-2.5"
      data-testid="dg-state-badge">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--qn-primary)]">
        {t("decisionGraph.stateOf", { n: index + 1 })}{live ? ` · ${t("decisionGraph.live.badgeSuffix")}` : ""}
      </div>
      <div className="text-sm font-semibold text-[var(--qn-foreground)]" data-testid="dg-state-title">{t(`decisionGraph.states.${s.key}.title`)}</div>
      <div className="text-xs text-[var(--qn-foreground-muted)] max-w-xs">{t(`decisionGraph.states.${s.key}.desc`)}</div>
    </motion.div>
  );
}
